/**
 * The patient's booking calendar — a port of `renderPatientGlobalCalendar`
 * (patient_dashboard.js:1013).
 *
 * Same month grid the doctor sees, in the patient's emerald palette: three
 * months stacked (this one and the next two), Saturday-first weekday headers,
 * a clinic-coloured tint that brightens with availability, the offer and
 * extra-hours pills, and a `متاح N | محجوز N` strip along the bottom of each day.
 *
 * It replaces a horizontal strip of thirty day-cards. That strip was the source
 * of the layout break: it sat in a CSS grid column, and a grid item defaults to
 * `min-width: auto`, so instead of scrolling inside its panel it stretched the
 * column and pushed the page sideways under the sidebar.
 *
 * A day is only clickable when it can actually be booked — a working day, not
 * past, not cancelled, not full. The legacy calendar attached its click handler
 * on exactly those conditions; here the same test also drives `disabled`, so
 * the day is unreachable by keyboard too rather than merely inert to the mouse.
 */

import { useMemo } from 'react';
import { Icon } from '../../components/common';
import { buildMonthStats } from '../../domain/calendar';
import {
  ARABIC_MONTHS,
  BADGE_EXTRA,
  BADGE_EXTRA_SHADOW,
  BADGE_OFFER,
  BADGE_OFFER_SHADOW,
  WEEKDAY_HEADERS,
  clinicTintStyle,
  leadingBlanks,
} from '../doctor/calendar/legacyTheme';
import { todayKey } from '../../domain/dates';

/** How many months the patient can look ahead. The legacy value was 3. */
const MONTHS_AHEAD = 3;

/** A day with no clinic working — the legacy's inert slate. */
const DAY_CLOSED = {
  backgroundColor: 'rgba(30, 41, 59, 0.3)',
  borderColor: 'rgba(255, 255, 255, 0.05)',
};

/** Every slot taken — rose, and not clickable. */
const DAY_FULL = {
  backgroundColor: 'rgba(244, 63, 94, 0.1)',
  borderColor: 'rgba(244, 63, 94, 0.3)',
};

export function BookingCalendar({
  clinics = [],
  bookings = [],
  offers = [],
  extraHours = [],
  cancelledDays = [],
  selectedDate,
  onSelectDay,
}) {
  const months = useMemo(() => {
    const now = new Date();
    return Array.from({ length: MONTHS_AHEAD }, (_, offset) => {
      const date = new Date(now.getFullYear(), now.getMonth() + offset, 1);
      return { year: date.getFullYear(), month: date.getMonth() };
    });
  }, []);

  return (
    <div className="space-y-6">
      {months.map(({ year, month }) => (
        <MonthPanel
          key={`${year}-${month}`}
          year={year}
          month={month}
          clinics={clinics}
          bookings={bookings}
          offers={offers}
          extraHours={extraHours}
          cancelledDays={cancelledDays}
          selectedDate={selectedDate}
          onSelectDay={onSelectDay}
        />
      ))}
    </div>
  );
}

function MonthPanel({
  year,
  month,
  clinics,
  bookings,
  offers,
  extraHours,
  cancelledDays,
  selectedDate,
  onSelectDay,
}) {
  const today = todayKey();

  const stats = useMemo(
    () => buildMonthStats({ year, month, clinics, bookings, offers, extraHours }),
    [year, month, clinics, bookings, offers, extraHours],
  );

  const blanks = leadingBlanks(new Date(year, month, 1).getDay());
  const cells = [...stats.values()];

  return (
    <div className="glass-panel relative overflow-hidden rounded-2xl border border-white/5 p-4 sm:p-6">
      <div className="mb-6 flex items-center justify-between">
        <h3 className="flex flex-wrap items-center gap-2 text-lg font-bold text-white sm:text-xl">
          <Icon name="calendar-range" size={24} className="text-emerald-400" />
          المواعيد المتاحة
          <span className="rounded-lg border border-white/10 bg-white/5 px-2 py-1 text-xs font-normal text-slate-400">
            {ARABIC_MONTHS[month]} {year}
          </span>
        </h3>
      </div>

      <div className="mb-2 grid grid-cols-7 gap-1 text-center text-[11px] font-bold text-slate-500 sm:text-sm md:gap-2">
        {WEEKDAY_HEADERS.map((label) => (
          <div key={label}>{label}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1 md:gap-2">
        {Array.from({ length: blanks }, (_, i) => (
          <div key={`blank-${i}`} className="aspect-square bg-transparent" />
        ))}

        {cells.map((cell) => (
          <BookingDay
            key={cell.dateKey}
            cell={cell}
            isToday={cell.dateKey === today}
            isPast={cell.dateKey < today}
            isSelected={cell.dateKey === selectedDate}
            isCancelled={cancelledDays.includes(cell.dateKey)}
            onSelect={onSelectDay}
          />
        ))}
      </div>
    </div>
  );
}

function BookingDay({ cell, isToday, isPast, isSelected, isCancelled, onSelect }) {
  const {
    dateKey,
    day,
    totalSlots,
    bookedSlots,
    availableSlots,
    hasOffer,
    offerName,
    offerPercent,
    hasExtraHours,
    extraDiscount,
    workingClinics,
    isFull,
  } = cell;

  const isOpen = workingClinics.length > 0 || hasExtraHours;
  const bookable = isOpen && !isPast && !isCancelled && !isFull;

  let style;
  let stateClass;

  if (isPast) {
    style = {};
    stateClass = 'grayscale opacity-60 bg-slate-900/50 border-slate-800 cursor-not-allowed';
  } else if (!isOpen) {
    style = DAY_CLOSED;
    stateClass = 'opacity-50 cursor-not-allowed';
  } else if (isFull) {
    style = DAY_FULL;
    stateClass = 'grayscale-[30%] cursor-not-allowed';
  } else {
    const availability = totalSlots > 0 ? (availableSlots / totalSlots) * 100 : 0;
    // Patient-side clinics default to emerald where the doctor's default cyan.
    style = clinicTintStyle(workingClinics[0]?.color || 'emerald', availability);
    stateClass = 'cursor-pointer hover:bg-slate-800/80';
  }

  if (bookable && isSelected) {
    stateClass += ' ring-2 ring-emerald-400 bg-emerald-500/20 shadow-[0_0_15px_rgba(16,185,129,0.3)]';
  } else if (bookable && isToday) {
    stateClass += ' ring-1 ring-emerald-500/50 shadow-[0_0_10px_rgba(16,185,129,0.1)]';
  }

  const dayNumberColor = isSelected
    ? 'text-white'
    : isPast
      ? 'text-slate-600'
      : isToday
        ? 'text-emerald-400'
        : 'text-slate-400';

  const label = isCancelled
    ? `${dateKey} — يوم ملغي`
    : isFull
      ? `${dateKey} — مكتمل الحجز`
      : isOpen && !isPast
        ? `${dateKey} — ${availableSlots} موعد متاح`
        : dateKey;

  return (
    <button
      type="button"
      disabled={!bookable}
      onClick={() => bookable && onSelect?.(dateKey, workingClinics[0]?.id ?? null)}
      title={label}
      aria-label={label}
      aria-pressed={isSelected}
      className={`group relative flex aspect-square flex-col overflow-hidden rounded-xl border transition-all ${stateClass}`}
      style={style}
    >
      <span
        className={`absolute right-1 top-1 z-10 text-sm font-bold md:right-2 md:top-2 md:text-base ${dayNumberColor}`}
      >
        {day}
      </span>

      {/* Badges sit in the middle, clear of the day number and the stats bar */}
      <span className="flex flex-1 flex-col items-center justify-center gap-1 px-1 pb-5 pt-5">
        {hasOffer && !isFull && (
          <span
            className={`z-20 flex w-fit max-w-full flex-col items-center rounded-full px-2 py-1 text-[10px] font-black leading-tight text-white shadow-lg ${
              isPast ? 'opacity-50' : 'animate-heartbeat'
            }`}
            style={{ background: BADGE_OFFER, boxShadow: BADGE_OFFER_SHADOW }}
          >
            {offerName && (
              <span className="max-w-full truncate" dir="rtl">
                {offerName}
              </span>
            )}
            {offerPercent > 0 && <span className="text-white/90">خصم {offerPercent}%</span>}
          </span>
        )}

        {hasExtraHours && !isFull && (
          <span
            className={`z-20 flex w-fit max-w-full items-center gap-1 rounded-full px-2 py-1 text-[10px] font-black leading-tight text-white shadow-lg ${
              isPast ? 'opacity-40' : 'animate-heartbeat'
            }`}
            style={{ background: BADGE_EXTRA, boxShadow: BADGE_EXTRA_SHADOW }}
          >
            <Icon name="zap" size={10} className="shrink-0 fill-white" />
            <span className="truncate">
              ساعات إضافية{extraDiscount > 0 ? ` · ${extraDiscount}%` : ''}
            </span>
          </span>
        )}
      </span>

      {isPast && (
        <span className="absolute bottom-1 left-1 text-[10px] font-bold text-slate-600 md:bottom-2 md:left-2 md:text-xs">
          منتهي
        </span>
      )}

      {!isPast && isFull && (
        <span className="absolute inset-0 z-30 flex items-center justify-center rounded-xl bg-black/40 backdrop-blur-[1px]">
          <span className="-rotate-[15deg] rounded-lg border border-rose-500/30 bg-slate-900/90 px-2 py-1 text-xs font-bold text-rose-500 md:text-sm">
            مكتمل الحجز
          </span>
        </span>
      )}

      {!isPast && !isFull && isOpen && (
        <span className="absolute inset-x-0.5 bottom-0.5 flex items-center justify-between rounded border border-white/5 bg-slate-900/80 px-1 py-0.5 text-[9px] font-bold text-slate-300 shadow-lg backdrop-blur-sm">
          <span className="flex items-center gap-0.5 text-emerald-400">
            <span className="inline-block h-1 w-1 rounded-full bg-emerald-500 shadow-[0_0_5px_rgba(16,185,129,0.5)]" />
            متاح {availableSlots}
          </span>
          <span className="mx-0.5 text-[6px] font-thin text-slate-600">|</span>
          <span className="flex items-center gap-0.5 text-rose-400">
            <span className="inline-block h-1 w-1 rounded-full bg-rose-500 shadow-[0_0_5px_rgba(244,63,94,0.5)]" />
            محجوز {bookedSlots}
          </span>
        </span>
      )}

      {isCancelled && (
        <span
          className="absolute inset-0 z-30 flex items-center justify-center rounded-xl bg-black/60 backdrop-blur-sm"
          title="هذا اليوم ملغي"
        >
          <span className="text-2xl font-bold text-white opacity-80 md:text-4xl">❌</span>
          <span className="absolute bottom-1 whitespace-nowrap rounded-full bg-black/80 px-1.5 py-0.5 text-[8px] font-bold text-white md:bottom-2 md:px-2 md:text-[10px]">
            يوم ملغي
          </span>
        </span>
      )}
    </button>
  );
}

export default BookingCalendar;
